import { Results, SelfieSegmentation } from '@mediapipe/selfie_segmentation';

import { logger } from '@gem/modules/logger';

import { WorkerMessageType } from './LiveKitProviderProcessor.types';

export interface TrackProcessor {
  name: string;
  init: (opts: any) => Promise<void>;
  restart: (opts: any) => Promise<void>;
  destroy: () => Promise<void>;
  processedTrack?: MediaStreamTrack;
  onPublish?: () => Promise<void>;
  onUnpublish?: () => Promise<void>;
}

export class LiveKitBlurProcessor implements TrackProcessor {
  private canvasCtx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private selfieSegmentation: SelfieSegmentation;
  private blurValue = 0;
  private worker: Worker;
  private incomingVideo: HTMLVideoElement | null = null;
  isStarted = false;
  name: 'blur-processor';
  processedTrack?: MediaStreamTrack;

  constructor() {
    this.worker = new Worker(new URL('./LiveKitProviderProcessor.worker.timer', import.meta.url));
    this.canvas = document.createElement('canvas');
    this.canvasCtx = this.canvas.getContext('2d')!;
    this.selfieSegmentation = new SelfieSegmentation({
      locateFile: (file: string): string =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
    });
    this.selfieSegmentation.setOptions({
      modelSelection: 1,
    });

    this.selfieSegmentation
      .initialize()
      .then(() => {
        console.log('selfieSegmentation initialized');
      })
      .catch((e) => {
        logger.get('liveKitBlurProcessor').error('selfieSegmentation initialize error', e);
      });

    this.selfieSegmentation.onResults(this.onGetImageResults.bind(this));
  }

  private onGetImageResults(results: Results): void {
    this.canvasCtx.save();
    this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // отрисовка маски лица
    this.canvasCtx.drawImage(results.segmentationMask, 0, 0, this.canvas.width, this.canvas.height);

    // отрисовка слоя с кадром, с вырезанием из него маски лица и установкой блюра
    this.canvasCtx.globalCompositeOperation = 'source-in';
    this.canvasCtx.drawImage(results.image, 0, 0, this.canvas.width, this.canvas.height);
    this.canvasCtx.filter = 'blur(0)';

    // отрисовка на пустом месте от маски незаблюренного изображения.
    this.canvasCtx.globalCompositeOperation = 'destination-atop';
    this.canvasCtx.filter = `blur(${this.blurValue}px)`;
    this.canvasCtx.drawImage(results.image, 0, 0, this.canvas.width, this.canvas.height);

    this.canvasCtx.restore();
  }

  private async processFrames(): Promise<void> {
    if (!this.incomingVideo) {
      return;
    }

    // eslint-disable-next-line no-constant-condition
    while (this.isStarted) {
      const promise = new Promise<void>((resolve, _reject) => {
        this.worker.onmessage = (): void => {
          this.worker.onmessage = null;
          resolve();
        };
        this.worker.postMessage({
          type: WorkerMessageType.Start,
        });
      });

      await promise;

      if (this.isStarted) {
        await this.selfieSegmentation.send({ image: this.incomingVideo });
      }
    }
  }

  async init(options: any): Promise<void> {
    const mediaStream = this.canvas.captureStream(60);
    this.processedTrack = mediaStream.getVideoTracks()[0];
    if (!options.element) {
      return;
    }

    this.incomingVideo = options.element;
    this.isStarted = true;

    options.element.addEventListener('loadeddata', () => {
      this.processFrames();
    });
  }

  setBlurValue(value: number): void {
    this.blurValue = value;
  }

  setTrackConstraints(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  async restart(): Promise<void> {
    this.isStarted = true;
    this.processFrames();
  }

  async destroy(): Promise<void> {
    this.isStarted = false;
  }

  async dispose(): Promise<void> {
    this.destroy();
    try {
      await this.selfieSegmentation.close();
    } catch (e) {
      logger.get('liveKitBlurProcessor').error('selfieSegmentation destroy error', e);
      console.log(e);
    }

    if (this.processedTrack) {
      this.processedTrack.stop();
    }
  }
}
